import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Checkbox, FormControlLabel, Alert, Table, TableBody, TableRow, TableCell,
} from '@mui/material';
import { getProject } from '../../api/annotatorProjects';
import { exportProject } from '../../api/annotatorExport';
import type { AnnotatorProject, ExportResult } from '../../types/annotator';

export default function AnnotatorExportPage() {
  const { projectId } = useParams();
  const [project, setProject] = useState<AnnotatorProject | null>(null);
  const [result, setResult] = useState<ExportResult | null>(null);
  const [includeUnannotated, setIncludeUnannotated] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    getProject(Number(projectId)).then(setProject).catch(console.error);
  }, [projectId]);

  async function handleExport(e: React.FormEvent) {
    e.preventDefault();
    if (!projectId) return;
    setError('');
    setResult(null);
    setLoading(true);
    try {
      const res = await exportProject(Number(projectId), includeUnannotated);
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Export failed.');
    } finally {
      setLoading(false);
    }
  }

  if (!project) return <Typography color="text.secondary">Loading…</Typography>;

  const annotatedCount = project.annotated_count;
  const unannotatedCount = project.image_count - project.annotated_count;
  const exportCount = includeUnannotated ? project.image_count : annotatedCount;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Button component={RouterLink} to={`/annotator/projects/${projectId}`} size="small" sx={{ mb: 0.5 }}>
          ← {project.name}
        </Button>
        <Typography variant="h5" fontWeight={600}>Export Dataset</Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleExport}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Output Directory</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
              {project.output_folder ?? '(not set)'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              YOLO folder structure will be created here.{' '}
              <RouterLink to={`/annotator/projects/${projectId}`} style={{ color: 'inherit' }}>Edit in Project Settings →</RouterLink>
            </Typography>

            <FormControlLabel
              control={<Checkbox checked={includeUnannotated} onChange={(e) => setIncludeUnannotated(e.target.checked)} />}
              label="Include unannotated images as negative samples"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mb: 2 }}>
              Unannotated images are exported with empty YOLO label files, which can help reduce false positives.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {result && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Export complete for {result.project_name}
                <Typography variant="caption" display="block">{result.export_path}</Typography>
                <Typography variant="caption" display="block">
                  Exported {result.total_images} image(s)
                  {result.skipped_unannotated > 0 ? `, skipped ${result.skipped_unannotated} unannotated` : ''}
                </Typography>
              </Alert>
            )}

            <Button type="submit" variant="contained" disabled={loading || exportCount === 0}>
              {loading ? 'Exporting…' : 'Export'}
            </Button>
            {exportCount === 0 && <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>No images to export.</Typography>}
          </form>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Summary</Typography>
          <Table size="small">
            <TableBody>
              <TableRow><TableCell>Project</TableCell><TableCell>{project.name}</TableCell></TableRow>
              <TableRow><TableCell>Labels</TableCell><TableCell>{project.labels.map((l) => l.name).join(', ') || '—'}</TableCell></TableRow>
              <TableRow><TableCell>Total images</TableCell><TableCell>{project.image_count}</TableCell></TableRow>
              <TableRow><TableCell>Annotated</TableCell><TableCell>{project.annotated_count}</TableCell></TableRow>
              <TableRow><TableCell>Unannotated</TableCell><TableCell>{unannotatedCount}</TableCell></TableRow>
              <TableRow><TableCell>To export</TableCell><TableCell>{exportCount}</TableCell></TableRow>
            </TableBody>
          </Table>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" fontWeight={600}>Output structure</Typography>
            <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', mt: 0.5, color: 'text.secondary' }}>
{`${project.output_folder ?? '<output_dir>'}/
  images/
  labels/
  classes.txt
  dataset.yaml
  notes.json`}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
